import { httpClient } from '@/core/api/httpClient';
import { roleToApi, type MemberRole } from '@/core/constants/roles';
import type { MemberDtoPagedResult, MembersResponse, CreateInvitationPayload } from '../types';
import { adaptMemberDto } from '@/features/members/public';
import { clientMembersMock } from './clientMembers.mock';
import { env } from '@/core/config/env';
import { API_V1 } from '@/core/api/endpoints';

const USE_MOCK = env.VITE_USE_MOCK === 'true';

/** Params para GET /clients/{clientId}/members */
export interface ClientMembersQueryParams {
  Page?: number;
  PageSize?: number;
  SearchTerm?: string;
  SortBy?: string;
  SortAscending?: boolean;
}

/**
 * Fetches and manages members assigned to a client within the active organization.
 *
 * @remarks
 * X-Tenant-Id is injected automatically by httpClient. Do not add it manually.
 *
 * @example
 * const data = await clientMembersService.getAll('client-1', { Page: 1, PageSize: 20 });
 * await clientMembersService.toggle('client-1', 'cm-123', true);
 * await clientMembersService.remove('client-1', 'cm-123');
 */
export const clientMembersService = {
  /**
   * Fetches paginated members of a client with optional search filtering.
   *
   * @param clientId - ID of the client whose members are queried
   * @param params - Pagination and search filters
   * @param currentUserId - ID of the current user to mark isCurrentUser
   * @returns Paginated list of adapted members
   *
   * @remarks
   * - Uses adaptMemberDto from features/members to normalize backend DTOs
   * - In mock mode, filters by clientId and SearchTerm locally
   *
   * @example
   * const { items, total, totalPages } = await clientMembersService.getAll(
   *   'client-123',
   *   { Page: 1, PageSize: 10, SearchTerm: 'john' },
   *   'user-456'
   * );
   */
  getAll: async (
    clientId: string,
    params: ClientMembersQueryParams,
    currentUserId?: string,
    signal?: AbortSignal,
  ): Promise<MembersResponse> => {
    if (USE_MOCK) {
      let items = clientMembersMock
        .filter((m) => m.clientId === clientId)
        .map((m) => ({
          ...m,
          isCurrentUser: !!currentUserId && m.userId === currentUserId,
        }));

      if (params.SearchTerm) {
        const term = params.SearchTerm.toLowerCase();
        items = items.filter(
          (m) => m.displayName.toLowerCase().includes(term) || m.email.toLowerCase().includes(term),
        );
      }

      const page = params.Page ?? 1;
      const pageSize = params.PageSize ?? 20;
      const total = items.length;
      const totalPages = Math.ceil(total / pageSize);
      const start = (page - 1) * pageSize;

      return { items: items.slice(start, start + pageSize), total, totalPages };
    }

    const { data } = await httpClient.get<MemberDtoPagedResult>(
      `${API_V1}/tenants/clients/${clientId}/members`,
      { params, signal },
    );

    return {
      items: (data.items ?? []).map((dto) => adaptMemberDto(dto, currentUserId)),
      total: data.totalCount,
      totalPages: data.totalPages,
    };
  },

  /**
   * Activates or deactivates a member within the client.
   *
   * @param _clientId - Client ID (not used in the URL; backend scopes via X-Tenant-Id)
   * @param memberId - ID of the member to modify
   * @param activate - true to activate, false to deactivate
   *
   * @remarks
   * - Uses PATCH, not PUT
   * - clientId is not sent in the URL; the backend determines org scope via X-Tenant-Id
   *
   * @example
   * await clientMembersService.toggle('client-123', 'member-456', true);
   */
  toggle: async (_clientId: string, memberId: string, activate: boolean): Promise<void> => {
    if (USE_MOCK) {
      const member = clientMembersMock.find((m) => m.id === memberId);
      if (member) member.isActive = activate;
      return;
    }

    await httpClient.patch<{ activate: boolean }, void>(
      `${API_V1}/tenants/members/${memberId}/toggle`,
      {
        activate,
      },
    );
  },

  /**
   * Removes a member from the client.
   *
   * @param _clientId - Client ID (not used in the URL; backend scopes via X-Tenant-Id)
   * @param memberId - ID of the member to remove
   *
   * @remarks
   * clientId is not sent in the URL; the backend determines org scope via X-Tenant-Id
   *
   * @example
   * await clientMembersService.remove('client-123', 'member-456');
   */
  remove: async (_clientId: string, memberId: string): Promise<void> => {
    if (USE_MOCK) {
      const index = clientMembersMock.findIndex((m) => m.id === memberId);
      if (index !== -1) clientMembersMock.splice(index, 1);
      return;
    }

    await httpClient.delete(`${API_V1}/tenants/members/${memberId}`);
  },
};

/**
 * Sends invitations to add new members to a client.
 *
 * @remarks
 * X-Tenant-Id is injected automatically by httpClient. Do not add it manually.
 *
 * @example
 * await clientInvitationsService.create('client-123', { email: 'new@example.com', clientId: 'client-123' });
 */
export const clientInvitationsService = {
  /**
   * Sends an email invitation to join the client.
   *
   * @param _clientId - Client ID (not used in the URL; sent in the payload instead)
   * @param payload - Invitation data including clientId and email
   *
   * @remarks
   * clientId is included in the payload, not in the endpoint URL
   *
   * @example
   * await clientInvitationsService.create('client-123', {
   *   email: 'new@example.com',
   *   clientId: 'client-123',
   *   role: 'client'
   * });
   */
  create: async (clientId: string, payload: CreateInvitationPayload): Promise<void> => {
    if (USE_MOCK) return;

    await httpClient.post<CreateInvitationPayload, void>(`${API_V1}/tenants/invitations`, {
      ...payload,
      role: roleToApi(payload.role as MemberRole),
      clientId,
    });
  },
};
